import { useEffect, useRef, useState, useTransition } from "react";

import Button from "../Button";
import Dialog from "../Dialog";

interface ChatRenameDialogProps {
	open: boolean;
	currentTitle: string;
	onClose: () => void;
	onRename: (title: string) => void | Promise<void>;
}

const ChatRenameDialog = ({
	open,
	currentTitle,
	onClose,
	onRename,
}: ChatRenameDialogProps) => {
	const [title, setTitle] = useState(currentTitle);
	const [isPending, startTransition] = useTransition();
	const inputRef = useRef<HTMLInputElement>(null);

	// Reset the field to the latest title each time the dialog opens
	useEffect(() => {
		if (open) {
			setTitle(currentTitle);
			requestAnimationFrame(() => inputRef.current?.select());
		}
	}, [open, currentTitle]);

	const commit = () => {
		const trimmed = title.trim();
		if (!trimmed || trimmed === currentTitle) {
			onClose();
			return;
		}
		startTransition(async () => {
			await onRename(trimmed);
			onClose();
		});
	};

	return (
		<Dialog
			title="Rename conversation"
			isOpen={open}
			disabled={isPending}
			onClose={onClose}
			elevated
			className="max-w-md"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					commit();
				}}
				className="flex flex-col gap-4"
			>
				<input
					ref={inputRef}
					type="text"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					disabled={isPending}
					maxLength={120}
					className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-slate-900 text-sm outline-none transition-colors focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-500/30 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:bg-slate-800"
				/>

				<div className="flex justify-end gap-3">
					<Button
						type="button"
						variant="outlined"
						disabled={isPending}
						onClick={onClose}
						className="flex-1"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						disabled={isPending || !title.trim()}
						className="flex-1"
					>
						Save
					</Button>
				</div>
			</form>
		</Dialog>
	);
};

export default ChatRenameDialog;
